import { useState } from "react";
import { SEVERITY_PALETTE } from "@safers/ui";
import { useEventAlarm } from "@/hooks/useEventAlarm";
import { useEventModal } from "@/hooks/useEventModal";
import { useCommandModalStore, selectCloseCommandModal } from "@/stores";
import { SEVERITY_LABELS } from "@/lib/map";

type Held = { alarm: NonNullable<ReturnType<typeof useEventAlarm>["alarm"]>; siteName?: string };

const reduceMotion = () =>
  typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

const SEV = {
  CRITICAL: { label: SEVERITY_LABELS.CRITICAL, ...SEVERITY_PALETTE.CRITICAL },
  WARNING: { label: SEVERITY_LABELS.WARNING, ...SEVERITY_PALETTE.WARNING },
} as const;

/**
 * 실시간 이벤트 알람 배너.
 *
 * `App` 이 라우트 밖에서 그리므로 **어느 화면 위에도** 뜬다. 그래서 위치를 공용
 * 헤더(`SiteShellHeader`, 3.75rem + 상단 안전영역) 아래로 잡는다 — `top-0` 이면 헤더를 통째로 덮어
 * 로고·현장명·알람 벨·사용자가 배너가 떠 있는 동안 눌리지 않는다. 배너 z(100)가
 * 헤더 z(30)보다 높아 화면 폭이 좁을수록(모바일) 헤더 전체가 가려진다.
 *
 * 헤더가 없는 화면(`/health`·`/login`)에서는 그만큼 아래에서 시작한다 — 가림을
 * 막는 쪽이 우선이다.
 */
export function EventAlarmBanner() {
  const { openEvent } = useEventModal();
  const closeCommand = useCommandModalStore(selectCloseCommandModal);
  const { alarm, site, dismiss } = useEventAlarm();

  /*
    나가는 동안에도 그릴 게 있어야 하므로 마지막 알람을 붙들어 둔다. 훅이 `null` 로
    바뀌는 순간 요소를 지우면 사라지는 애니메이션을 걸 자리가 없다.

    이펙트가 아니라 **렌더 중 조정**이다(`cctv-ai` 의 `lastSiteId` 와 같은 방식) —
    이펙트로 하면 한 프레임 늦어 배너가 깜빡였다가 다시 뜬다.
  */
  const [held, setHeld] = useState<Held | null>(null);
  const [leaving, setLeaving] = useState(false);

  if (alarm && alarm.id !== held?.alarm.id) {
    setHeld({ alarm, siteName: site?.name });
    setLeaving(false);
  } else if (!alarm && held && !leaving) {
    // 동작 줄이기가 켜져 있으면 나가는 애니메이션이 없다 —
    // `animationend` 를 기다리면 배너가 영영 안 사라진다.
    if (reduceMotion()) setHeld(null);
    else setLeaving(true);
  }

  if (!held) return null;

  const shown = held.alarm;
  const shownSiteName = held.siteName;
  const sev = shown.severity === "CRITICAL" ? SEV.CRITICAL : SEV.WARNING;

  return (
    /*
      `pointer-events-auto` 가 필요하다. 모달·드로어(Radix)가 열리면 **`body` 에
      `pointer-events: none`** 이 걸려 바깥 클릭이 통째로 막힌다. 배너는 `z-[100]` 이라
      모달(z-50) 위에 그려지지만, 그리는 것과 누르는 것은 별개라 눌러도 아무 일이
      없었다 — 위험 알람이 보이는데 못 누르는 상태가 된다.

      바깥 여백은 `pointer-events-none` 으로 되돌린다. 안 그러면 배너 없는 좌우 공간이
      모달의 바깥 클릭(닫기)을 가로챈다.

      `top` 은 헤더와 같은 식을 쓴다. 헤더가 안전영역만큼 커지는데 여기가 3.75rem
      고정이면 배너가 헤더 밑으로 파고든다. 브라우저 탭에서는 env() 가 0 이라 원래
      값으로 돌아온다.
    */
    <div className="pointer-events-none fixed inset-x-0 top-[calc(3.75rem_+_env(safe-area-inset-top))] z-[100] flex justify-center px-3 pt-3 [&>*]:pointer-events-auto">
      {/*
        `key` 로 알람마다 새 요소를 만든다. 같은 노드를 재사용하면 등장 애니메이션이
        다시 돌지 않아, 앞 알람이 떠 있는 채로 **내용만 조용히 바뀐다** — 새 위험이
        온 걸 눈이 못 잡는다(교체는 8초 자동 해제와 별개로 일어난다).
      */}
      <div
        key={shown.id}
        role="alert"
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget && leaving) {
            setHeld(null);
            setLeaving(false);
          }
        }}
        onClick={() => {
          /*
            상세 모달을 연다. 예전에는 현장·CCTV 로 이동했는데, **이미 그 화면을 보고 있을 때**
            (예: `/cctv-ai/17` 에서 17번 현장 알람) 같은 자리로 이동해 아무 일도 일어나지
            않았다. 모달은 어디서 눌러도 내용을 보여준다.

            팔레트가 열려 있으면 **먼저 닫는다.** 안 닫으면 상세 시트가 그 위에 겹쳐
            모달이 둘이 되고 Esc 로도 하나씩만 닫힌다. 배너는 모달 위에도 뜨므로 이
            경로는 실제로 밟힌다.
          */
          closeCommand();
          openEvent(shown.id);
          dismiss();
        }}
        className={`${leaving ? "alarm-lift" : "alarm-drop"} flex w-full max-w-2xl cursor-pointer items-center gap-3 rounded-2xl border-2 bg-[var(--surface-base)] px-4 py-3 shadow-[0_0.75rem_1.75rem_-0.5rem_rgba(0,0,0,0.3)]`}
        style={{ borderColor: sev.fill }}
      >
        {/* 글자가 얹히므로 fill(500)이 아니라 chipBg+chipFg 를 쓴다 — severity.ts 의 규칙. */}
        <span
          className="flex-none rounded-md px-2 py-0.5 text-xs font-bold"
          style={{ background: sev.chipBg, color: sev.chipFg }}
        >
          {sev.label}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-bold" style={{ color: sev.panelFg }}>
            {shownSiteName ? `[${shownSiteName}] ` : ""}
            {shown.name}
          </div>
          <div className="truncate text-xs text-neutral-500">
            {shownSiteName ? "눌러서 상세 확인" : "실시간 이벤트가 감지되었습니다 · 눌러서 확인"}
          </div>
        </div>
        <button
          type="button"
          aria-label="알람 닫기"
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          className="-mr-1 flex size-8 flex-none items-center justify-center rounded-lg text-lg leading-none text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
